import { useState, type ReactNode } from 'react';
import { Lightbulb } from 'lucide-react';
import {
  SUGGESTION_ACTIONS,
  SUGGESTION_ACTION_LABELS,
  SUGGESTION_NOTE_MAX,
  formatSuggestionDecisions,
  type FactorySuggestions,
  type SuggestionAction,
  type SuggestionDecision,
} from '@shared/factory-suggestions';
import { TASK_CHAT_MAX } from '@shared/types/tasks';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { SegmentedControl } from '@/components/SegmentedControl';
import { StatusBadge, type StatusTone } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FoldingCard } from './FoldingCard';
import { useTaskDraft } from './task-drafts';

/** What each choice does, as its hover text. */
const ACTION_HINTS: Record<SuggestionAction, string> = {
  yes: 'The AI builder builds it on this task’s branch and PR',
  backlog: 'A new task for later',
  no: 'Record that we decided not to do it',
};

const ACTION_TONES: Record<SuggestionAction, StatusTone> = {
  yes: 'success',
  backlog: 'neutral',
  no: 'neutral',
};

/** A decision being made: the choice picked and its note. */
export interface SuggestionPick {
  action: SuggestionAction;
  note: string;
}

/**
 * T-55: the software factory's suggestions for further changes on a task (a `factory-suggestions`
 * block in its comment), as a card, copied from the factory's own. Anyone who can comment picks Yes
 * please, Backlog or No thanks for the ones they want to decide and applies them together, as one
 * comment the factory reads back; decided ones show their decision. Without `onApply` (a preview) it
 * only lists the suggestions and decisions.
 */
export function FactorySuggestionsCard({
  suggestions,
  decided,
  meta,
  onApply,
  messageRef = null,
  from = 'the AI builder',
  fold = false,
  id,
}: {
  suggestions: FactorySuggestions;
  /** Decisions so far, by suggestion number (1-based). */
  decided?: ReadonlyMap<number, SuggestionDecision>;
  /** Who and when, at the end of the header line. */
  meta?: ReactNode;
  /** Posts the decisions as one comment (formatSuggestionDecisions). */
  onApply?: (message: string) => void;
  /** Names this round in the decisions (decisionsMessageRef), when the task has more than one. */
  messageRef?: string | null;
  /** Who suggested them, in the header. */
  from?: string;
  /** Starts folded to its header line, which opens it (a decided round in the task's Decisions). */
  fold?: boolean;
  /** For a pointer in the comments to scroll to it. */
  id?: string;
}) {
  const [picks, setPicks] = useState<Record<number, SuggestionPick>>({});
  const list = suggestions.suggestions;
  const open = list.filter((_s, i) => !decided?.has(i + 1)).length;
  const decidedCount = list.length - open;
  const chosen = Object.entries(picks)
    .map(([n, pick]) => ({ number: Number(n), ...pick }))
    .filter((p) => !decided?.has(p.number));

  const set = (number: number, change: Partial<SuggestionPick> | null) =>
    setPicks((prev) => {
      const next = { ...prev };
      if (change === null) delete next[number];
      else next[number] = { ...(prev[number] ?? { action: SUGGESTION_ACTIONS[0], note: '' }), ...change };
      return next;
    });

  const message = formatSuggestionDecisions(
    chosen.map((p) => ({ ...p, title: list[p.number - 1]?.title ?? '' })),
    messageRef
  );
  // One comment holds them all: past the limit the server would cut it short, so it isn't sent.
  const tooLong = message.length > TASK_CHAT_MAX;
  // Picked and not applied: leaving the page or closing the task asks first.
  const dirty = Boolean(onApply) && chosen.length > 0;
  useUnsavedChangesGuard(dirty, 'Your decisions on the suggestions aren’t sent yet. Leave without sending them?');
  useTaskDraft(dirty);

  const apply = () => {
    if (!onApply || !chosen.length || tooLong) return;
    onApply(message);
    setPicks({});
  };

  return (
    <FoldingCard
      id={id}
      fold={fold}
      summary={decidedCount ? decisionsSummary(list.length, decided) : undefined}
      header={
        <>
          <Lightbulb className="size-4" aria-hidden />
          {list.length === 1 ? `A suggestion from ${from}` : `${list.length} suggestions from ${from}`}
          {open === 0 ? <Badge variant="secondary">Decided</Badge> : null}
          {meta ? <span className="ml-auto font-normal">{meta}</span> : null}
        </>
      }
    >
      <ol className="space-y-3">
        {list.map((s, i) => {
          const number = i + 1;
          const decision = decided?.get(number);
          const pick = picks[number];
          return (
            <li key={s.id} className="space-y-1.5">
              <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                <p className="min-w-0 flex-1 text-sm">
                  <span className="text-muted-foreground">{number}. </span>
                  <span className="font-medium">{s.title}</span>
                </p>
                {decision ? (
                  <StatusBadge tone={ACTION_TONES[decision.action]} size="sm" dot>
                    {SUGGESTION_ACTION_LABELS[decision.action]}
                  </StatusBadge>
                ) : null}
              </div>
              {s.detail ? <p className="whitespace-pre-wrap break-words pl-4 text-xs text-muted-foreground">{s.detail}</p> : null}
              {decision ? (
                decision.note ? (
                  <p className="break-words pl-4 text-xs">
                    <span className="text-muted-foreground">Why: </span>
                    {decision.note}
                  </p>
                ) : null
              ) : onApply ? (
                <div className="space-y-1.5 pl-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <SegmentedControl
                      size="xs"
                      wrap
                      aria-label={`What to do with “${s.title}”`}
                      value={pick?.action ?? null}
                      onValueChange={(action) => set(number, { action })}
                      options={SUGGESTION_ACTIONS.map((a) => ({ value: a, label: SUGGESTION_ACTION_LABELS[a], hint: ACTION_HINTS[a] }))}
                    />
                    {pick ? (
                      <Button variant="link" size="inline" className="text-xs" onClick={() => set(number, null)}>
                        Clear
                      </Button>
                    ) : null}
                  </div>
                  {pick?.action === 'no' ? (
                    <Input
                      aria-label={`Why not “${s.title}”`}
                      placeholder="Why not? (optional)"
                      maxLength={SUGGESTION_NOTE_MAX}
                      value={pick.note}
                      onChange={(e) => set(number, { note: e.target.value })}
                    />
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
      {onApply && open > 0 ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {tooLong ? (
            <span className="text-xs text-destructive">Too long for one comment: shorten your notes</span>
          ) : (
            <span className="text-xs text-muted-foreground">
              {chosen.length ? `${chosen.length} of ${open} picked` : 'Pick what to do with each suggestion'}
            </span>
          )}
          <Button size="sm" disabled={!chosen.length || tooLong} onClick={apply}>
            Apply
          </Button>
        </div>
      ) : null}
    </FoldingCard>
  );
}

/** What was decided, on one line for a folded round: "1 Yes please · 1 No thanks". */
function decisionsSummary(total: number, decided?: ReadonlyMap<number, SuggestionDecision>): string | undefined {
  if (!decided?.size) return undefined;
  const counts = new Map<SuggestionAction, number>();
  for (const d of decided.values()) counts.set(d.action, (counts.get(d.action) ?? 0) + 1);
  const parts = SUGGESTION_ACTIONS.filter((a) => counts.has(a)).map((a) => `${counts.get(a)} ${SUGGESTION_ACTION_LABELS[a]}`);
  const left = total - decided.size;
  return [...parts, ...(left > 0 ? [`${left} to decide`] : [])].join(' · ');
}

/** In the comments, the factory's suggestions as one line pointing to the task's Decisions, where they're decided. */
export function FactorySuggestionsPointer({
  count,
  decided = false,
  onShow,
}: {
  count: number;
  decided?: boolean;
  /** Scrolls to the suggestions (showFoldingCard). */
  onShow: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
      <Lightbulb className="size-4 shrink-0" aria-hidden />
      <span>{count === 1 ? 'Suggested a further change' : `Suggested ${count} further changes`}</span>
      {decided ? <Badge variant="secondary">Decided</Badge> : null}
      <Button variant="link" size="inline" onClick={onShow}>
        {decided ? 'See Decisions' : 'Decide under the description'}
      </Button>
    </div>
  );
}
